import {
  AccountBookOutlined,
  ApiOutlined,
  CloudServerOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DownOutlined,
  LogoutOutlined,
  RightOutlined,
  SettingOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { platformGetEnvironment } from '../api'
import { useAuth } from '../AuthContext'
import { useTranslation } from 'react-i18next'
import ThemeToggle from '../ui/ThemeToggle'
import '../ui/tenant-shell.css'
import type { PlatformEnvironmentIdentity } from '../types'
import {
  PLATFORM_NAV,
  initialExpandedPlatformNavGroups,
  type PlatformNavGroup,
  type PlatformNavItem,
} from './navConfig'
import { isPlatformNavGroupExpanded, isPlatformNavItemActive, togglePlatformNavGroupExpanded } from './platformNavState'
import './platform-shell.css'

const GROUP_ICONS: Record<string, ReactNode> = {
  root: <DashboardOutlined />,
  tenant: <TeamOutlined />,
  resource: <CloudServerOutlined />,
  ai: <ApiOutlined />,
  tasks: <UnorderedListOutlined />,
  system: <SettingOutlined />,
  billing: <AccountBookOutlined />,
  support: <CustomerServiceOutlined />,
}

function resolveItemLabel(item: PlatformNavItem, t: (key: string) => string): string {
  return item.labelKey ? t(item.labelKey) : item.label
}

function groupHasActiveChild(group: PlatformNavGroup, pathname: string, search: string): boolean {
  return group.items.some((item) => item.path && isPlatformNavItemActive(pathname, item.path, search))
}

/** Platform Admin shell — fixed sidebar + independently scrolling main content. */
export default function PlatformLayout() {
  const { t } = useTranslation('platformAdmin')
  const { me, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() =>
    initialExpandedPlatformNavGroups(PLATFORM_NAV),
  )

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => togglePlatformNavGroupExpanded(prev, groupKey))
  }, [])

  const [env, setEnv] = useState<PlatformEnvironmentIdentity | null>(null)
  useEffect(() => {
    platformGetEnvironment()
      .then((res) => setEnv(res.data))
      .catch(() => setEnv(null))
  }, [])

  const onLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="pf-shell">
      <aside className="pf-sidebar" aria-label="Platform Admin navigation">
        <div className="pf-sidebar-brand">
          <div className="pf-sidebar-logo">VISTOERA</div>
          <div className="pf-sidebar-badge">Platform Admin</div>
          {env && (
            <div
              className={`pf-sidebar-env ${env.environment === 'production' ? 'pf-sidebar-env--production' : 'pf-sidebar-env--preview'}`}
              title="环境身份来自部署配置（resource_environment / APP_ENV），不可在前端切换"
            >
              环境：{env.environment_label}
            </div>
          )}
        </div>

        <nav className="pf-sidebar-nav">
          {PLATFORM_NAV.map((group) => {
            const expanded = isPlatformNavGroupExpanded(expandedGroups, group.key)
            const hasActiveChild = groupHasActiveChild(group, location.pathname, location.search)
            const groupIcon = GROUP_ICONS[group.key]

            return (
              <div
                key={group.key}
                className={`pf-nav-group${hasActiveChild ? ' pf-nav-group--has-active' : ''}`}
              >
                <button
                  type="button"
                  className="pf-nav-group-header"
                  aria-expanded={expanded}
                  aria-controls={`pf-nav-group-${group.key}`}
                  onClick={() => toggleGroup(group.key)}
                >
                  <span className="pf-nav-group-header-main">
                    {groupIcon && <span className="pf-nav-group-icon" aria-hidden="true">{groupIcon}</span>}
                    <span className="pf-nav-group-title">{group.title}</span>
                  </span>
                  <span className="pf-nav-group-chevron" aria-hidden="true">
                    {expanded ? <DownOutlined /> : <RightOutlined />}
                  </span>
                </button>

                {expanded && (
                  <div className="pf-nav-group-children" id={`pf-nav-group-${group.key}`}>
                    {group.items.map((item) => {
                      const itemLabel = resolveItemLabel(item, t)
                      const isActive = !!item.path && isPlatformNavItemActive(location.pathname, item.path, location.search)

                      if (item.disabled || !item.path) {
                        return (
                          <Tooltip key={item.key} title="尚未开发" placement="right">
                            <div className="pf-nav-link pf-nav-link--child pf-nav-link--disabled">
                              <span className="pf-nav-child-label">{itemLabel}</span>
                            </div>
                          </Tooltip>
                        )
                      }

                      return (
                        <Link
                          key={item.key}
                          to={item.path}
                          className={`pf-nav-link pf-nav-link--child${isActive ? ' pf-nav-link--active' : ''}`}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span className="pf-nav-child-label">{itemLabel}</span>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
      </aside>

      <div className="pf-main">
        <header className="pf-header">
          <div className="pf-header-actions">
            <ThemeToggle />
            <div className="pf-header-user">
              <div className="pf-header-email">{me?.user.email}</div>
              <div className="pf-header-role">{me?.is_platform_admin ? 'Platform Admin' : ''}</div>
            </div>
            <button type="button" className="pf-logout-btn" onClick={onLogout}>
              <LogoutOutlined /> 退出登录
            </button>
          </div>
        </header>
        <main className="pf-content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
